import { NetworkStatus } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useNetwork,
  useUpdateNetworkStatus,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useNetworkOverusage } from './hooks/useNetworkOverusage.js'

export const PublishSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { Link: RouterLink } = useRouter()
  const { isOverused } = useNetworkOverusage()

  const isPublished = network.status === NetworkStatus.published

  const { mutateAsync: updateNetworkStatus, isLoading } =
    useUpdateNetworkStatus()

  const unpublish = async () => {
    const confirmed = await confirm({
      title: $t({
        defaultMessage: 'Are you sure about unpublishing your site?',
        id: 'Admin.Publish.Unpublish.Confirm',
      }),
      description: $t({
        defaultMessage:
          'Proceeding will unpublish your site and make it inaccessible to all users except administrators. Are you sure you want to continue?',
        id: 'Admin.Publish.Unpublish.Confirm.Description',
      }),
      proceedLabel: $t({
        defaultMessage: 'Unpublish',
        id: 'Admin.Publish.Unpublish.Confirm.Proceed',
      }),
      danger: true,
    })
    if (!confirmed) {
      return
    }
    try {
      await updateNetworkStatus({
        input: {
          status: NetworkStatus.unpublished,
        },
      })
      toast({
        title: $t({
          defaultMessage: 'Your site is unpublished.',
          id: 'Admin.Publish.Unpublish.Success',
        }),
        status: 'success',
      })
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Unable to unpublish your site.',
          id: 'Admin.Publish.Unpublish.Error',
        }),
        status: 'error',
      })
    }
  }

  const publish = async () => {
    try {
      await updateNetworkStatus({
        input: {
          status: NetworkStatus.published,
        },
      })
      toast({
        title: $t({
          defaultMessage: 'Your site is published.',
          id: 'Admin.Publish.Publish.Success',
        }),
        status: 'success',
      })
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Unable to publish your site.',
          id: 'Admin.Publish.Publish.Error',
        }),
        status: 'error',
      })
    }
  }

  return (
    <Card id="publish" className="scroll-m-4">
      <Card.Header
        title={$t({
          defaultMessage: 'Publish settings',
          id: 'Admin.PublishSettings',
        })}
      />
      <Card.Content>
        {isPublished ? (
          <div>
            <p>
              <T
                defaultMessage="Your site is published and visible to everyone."
                id="Admin.Publish.Published"
              />
            </p>
            <div className="flex justify-end">
              <Button
                variant="secondaryNeutral"
                loading={isLoading}
                onClick={unpublish}
              >
                <T defaultMessage="Unpublish" id="Admin.Publish.Unpublish" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <p>
              <T
                defaultMessage="Your website is currently unpublished and can only be viewed by administrators."
                id="Admin.Publish.Unpublished"
              />
            </p>
            {isOverused && (
              <Alert status="info">
                <T
                  defaultMessage="You cannot publish your site because you have exceeded the usages in your plan. <link>Manage usages</link>."
                  id="Admin.Publish.Overused"
                  values={{
                    link: chunks => (
                      <Link as={RouterLink} href="/manage/billing/usages">
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </Alert>
            )}
            <div className="flex justify-end">
              <Button
                loading={isLoading}
                onClick={publish}
                disabled={isOverused}
              >
                <T defaultMessage="Publish" id="Admin.Publish.Publish" />
              </Button>
            </div>
          </div>
        )}
      </Card.Content>
    </Card>
  )
}
